import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Mail, Phone, User, Trash2, Eye, ArrowLeft, RefreshCw } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function AdminLogin({ password, setPassword, error, onSubmit, onBack }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-black tracking-tight text-gray-900 font-heading">
              ADMIN
            </span>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Wachtwoord"
              data-testid="admin-password-input"
              className="rounded-none h-12 font-body"
            />
            {error && <p className="text-red-600 text-sm font-body">{error}</p>}
            <Button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full rounded-none bg-red-600 hover:bg-red-700 text-white font-bold font-body h-12"
            >
              Inloggen
            </Button>
          </form>
          <button
            onClick={onBack}
            className="mt-4 text-sm text-gray-500 hover:text-gray-900 font-body flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Terug naar website
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageCard({ msg, onMarkRead, onDelete }) {
  return (
    <div
      data-testid={`message-${msg.id}`}
      className={`bg-white border p-5 transition-colors ${
        msg.read ? "border-gray-200" : "border-red-300 bg-red-50/30"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="flex items-center gap-1 text-sm font-bold text-gray-900 font-body">
              <User className="w-4 h-4 text-gray-400" />
              {msg.name}
            </span>
            <span className="flex items-center gap-1 text-sm text-gray-500 font-body">
              <Mail className="w-3 h-3" />
              {msg.email}
            </span>
            {msg.phone && (
              <span className="flex items-center gap-1 text-sm text-gray-500 font-body">
                <Phone className="w-3 h-3" />
                {msg.phone}
              </span>
            )}
          </div>
          <p className="text-gray-700 font-body text-sm leading-relaxed">
            {msg.message}
          </p>
          <p className="text-xs text-gray-400 font-body mt-2">
            {formatDate(msg.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!msg.read && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMarkRead(msg.id)}
              data-testid={`mark-read-${msg.id}`}
              className="rounded-none font-body gap-1 text-xs"
            >
              <Eye className="w-3 h-3" /> Gelezen
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(msg.id)}
            data-testid={`delete-${msg.id}`}
            className="rounded-none font-body text-red-600 hover:bg-red-50 border-red-200 gap-1 text-xs"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMessages() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`);
      setMessages(res.data);
    } catch {
      setMessages([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authenticated) fetchMessages();
  }, [authenticated, fetchMessages]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setAuthenticated(true);
        setError("");
      } else {
        setError("Ongeldig wachtwoord");
      }
    } catch {
      setError("Fout bij inloggen");
    }
  };

  const markRead = async (id) => {
    await axios.put(`${API}/contact/${id}/read`);
    setMessages(messages.map((m) => (m.id === id ? { ...m, read: true } : m)));
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit bericht wilt verwijderen?")) return;
    await axios.delete(`${API}/contact/${id}`);
    setMessages(messages.filter((m) => m.id !== id));
  };

  if (!authenticated) {
    return (
      <AdminLogin
        password={password}
        setPassword={setPassword}
        error={error}
        onSubmit={handleLogin}
        onBack={() => navigate("/")}
      />
    );
  }

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
              <span className="text-white font-black text-sm font-heading">R</span>
            </div>
            <span className="text-lg font-black tracking-tight text-gray-900 font-heading">
              BERICHTEN
            </span>
            {unread > 0 && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 font-body">
                {unread} nieuw
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchMessages}
              data-testid="refresh-btn"
              className="rounded-none font-body gap-2"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Vernieuwen
            </Button>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-gray-500 hover:text-gray-900 font-body flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Website
            </button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {messages.length === 0 ? (
          <div className="text-center py-24">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-400 font-body">Nog geen berichten ontvangen</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <MessageCard
                key={msg.id}
                msg={msg}
                onMarkRead={markRead}
                onDelete={deleteMessage}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
